import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';

interface Props {
  children: React.ReactNode;
  requiredRole?: 'user' | 'partner' | 'admin';
}

export default function ProtectedRoute({ children, requiredRole }: Props) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to the appropriate login page based on path
    if (location.pathname.startsWith('/admin')) return <Navigate to="/admin/login" replace />;
    if (location.pathname.startsWith('/partner')) return <Navigate to="/partner/login" replace />;
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Onboarding gate: redirect to /onboarding if not completed (user role only, skip certain paths)
  const onboardingExempt = ['/onboarding', '/terms', '/privacy', '/safety'].includes(location.pathname);
  if (
    user?.role === 'user' &&
    user?.onboarding_completed === false &&
    !onboardingExempt
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Admin can access everything
    if (user?.role === 'admin' && requiredRole !== 'admin') {
      return <>{children}</>;
    }
    // Wrong role — redirect to appropriate portal
    if (user?.role === 'partner') return <Navigate to="/partner" replace />;
    if (user?.role === 'admin') return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
